<template>
  <VDialogV3
    id="product-category-characteristics-dialog"
    hide-footer
    width="600px"
    height="100vh"
    position="right"
    :loading="false"
  >
    <template #header>
      <div class="header-wrap">
        <span>Добавление характеристик</span>
        <VInput
          v-model="searchQuery"
          placeholder="Поиск по названию"
          clearable
          @input="onSearchInput"
        />
      </div>
    </template>

    <VDataGridV2
      sticky
      layout="fill"
      selectable
      :data="data"
      :selected-rows="productCategory.characteristics"
      :disable-row-when="() => isLoading"
      @row:selected="attachCharacteristic"
      @row:deselected="detachCharacteristic"
    >
      <template #empty>
        Введите значение в поисковую строку
      </template>

      <VDataGridColumnV2
        id="id"
        label="ID"
        width="80px"
      />

      <VDataGridColumnV2
        id="name"
        label="Название"
      />

      <VDataGridColumnV2
        id="type"
        label="Тип"
        width="150px"
      >
        <template #default="{ row }">
          {{ row.valueTypeName }}
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="unit"
        label="Ед. изм"
        width="80px"
      >
        <template #default="{ row }">
          <template v-if="row.unit && row.unit.hasOwnProperty('name')">
            {{ row.unit.name === 'строка' ? '' : row.unit.name }}
          </template>
        </template>
      </VDataGridColumnV2>
    </VDataGridV2>
  </VDialogV3>
</template>

<style src="./ProductCategoryCharacteristicsDialog.scss" lang="scss"/>

<script lang="ts">

import ProductCategoriesService from '@/domains/ProductCategories/ProductCategoriesService';
import ProductCategory          from '@/domains/ProductCategories/ProductCategory';
import CharacteristicsService   from '@/domains/Characteristics/CharacteristicsService';
import GlobalLoader             from '@/core/services/GlobalLoader/GlobalLoader';

import type Characteristic from '@/domains/Characteristics/Characteristic';

export default defineComponent({
  name: 'ProductCategoryCharacteristicsDialog',

  props: {
    productCategory: {
      type:    Object as PropType<ProductCategory>,
      default: null,
    },
  },

  data() {
    return {
      data:        [] as Characteristic[],
      searchQuery: '' as string | null,
    };
  },

  computed: {
    /** Режим создания — мутируем сущность напрямую, не дёргая бэк */
    createMode(): boolean {
      return Boolean(!this.productCategory.id);
    },

    isLoading(): boolean {
      return GlobalLoader.isVisible;
    },
  },

  methods: {
    /**
     * Загружает характеристики по введённой строке поиска. Пустой ввод
     * очищает список (сохраняет UX «пока не введён запрос — пусто»).
     */
    async onSearchInput(value: string): Promise<void> {
      this.searchQuery = value || null;

      if (this.searchQuery === null) {
        this.data = [];
        return;
      }

      const { data } = await CharacteristicsService.getListPaginated({
        pattern: this.searchQuery,
      });

      this.data = data;
    },

    /**
     * Привязать характеристику к категории. В режиме создания — мутация
     * сущности; иначе — RPC и пополнение характеристик категории.
     */
    attachCharacteristic(characteristic: Characteristic): void {
      if (this.createMode) {
        this.productCategory.attachCharacteristic(characteristic);
        return;
      }

      ProductCategoriesService
        .attachCharacteristic(this.productCategory.id, characteristic.id)
        .then(() => {
          this.productCategory.characteristics.push(characteristic);
        });
    },

    /**
     * Отвязать характеристику от категории. Симметрично attachCharacteristic.
     */
    detachCharacteristic(characteristic: Characteristic): void {
      if (this.createMode) {
        this.productCategory.detachCharacteristic(characteristic);
        return;
      }

      ProductCategoriesService
        .detachCharacteristic(this.productCategory.id, characteristic.id)
        .then(() => {
          const index = this.productCategory.characteristics.findIndex(
            (item: Characteristic) => item.id === characteristic.id,
          );
          if (index >= 0) {
            this.productCategory.characteristics.splice(index, 1);
          }
        });
    },
  },
});
</script>
